import { Component, DestroyRef, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { HttpServiceService } from '../../services/http-service.service';
import { LoadingService } from '../../services/loading.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgxToastrService } from '../../_services/ngx-toastr/ngx-toastr.service';
import { Select2Data } from 'ng-select2-component';
import { Subject, debounceTime, tap, switchMap, Subscription, interval, takeWhile } from 'rxjs';
import { NgIf, NgFor, NgClass, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import { NgSelectModule } from '@ng-select/ng-select';
import { BreadcrumbComponent } from '../../elements/breadcrumb/breadcrumb.component';
import { PaginationComponent } from '../../elements/pagination/pagination.component';
import { USER_PERMISSIONS } from '../../app.constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  LeadsReportRow,
  LeadsReportSummary,
  ReportJobStatusResponse
} from '../../models/reports.model';
import { LEAD_STAGE_OPTIONS, LEAD_STATUS_OPTIONS } from '../../leads/lead.model';

@Component({
  selector: 'app-leads-reports',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    PaginationComponent,
    BreadcrumbComponent,
    NgSelectModule,
    NgIf,
    NgFor,
    NgClass,
    NgSwitch,
    NgSwitchCase,
    NgSwitchDefault
  ],
  templateUrl: './leads-reports.component.html',
  styleUrl: './leads-reports.component.css'
})
export class LeadsReportsComponent implements OnInit, OnDestroy {
  breadcrumbList = {
    breadcrumb_path: 'Reports',
    currentURL: 'Leads',
    breadcrumb_url: '/admin/reports/leads'
  };

  // Tab control
  activeTab: 'all' | 'by-status' | 'by-source' | 'graphs' = 'all';

  // Pagination - All tab
  pageAll: number = 1;
  totalRowsAll: number = 20;
  totalPageAll: number = 0;
  pageCountAll: number = 1;

  // Pagination - By Status tab
  pageByStatus: number = 1;
  totalRowsByStatus: number = 20;
  totalPageByStatus: number = 0;
  pageCountByStatus: number = 1;

  // Pagination - By Source tab
  pageBySource: number = 1;
  totalRowsBySource: number = 20;
  totalPageBySource: number = 0;
  pageCountBySource: number = 1;

  // Data
  allLeadsData: LeadsReportRow[] = [];
  byStatusData: any[] = [];
  bySourceData: any[] = [];
  summaryData: LeadsReportSummary | null = null;

  // Filter form
  filterForm!: FormGroup;
  disableClear: boolean = false;

  // Manager/assigned_to dropdown
  managersList: any[] = [];
  managersItems: Select2Data = [];
  searchManager$ = new Subject<string>();
  private managerPage = 1;
  private managerPageSize = 20;
  private hasMoreManagers = true;

  // Available columns (from server) and currently-checked column keys
  availableColumns: { key: string; label: string; default?: boolean; wrap?: boolean }[] = [];
  selectedColumnKeys: Set<string> = new Set();
  showColumnPicker = false;

  // Stage and Status options
  stageOptions = LEAD_STAGE_OPTIONS;
  statusOptions = LEAD_STATUS_OPTIONS;

  // Source options (must match the Leads module so report filters return data)
  sourceOptions = [
    { value: 'Website/Form', label: 'Website/Form (all websites)' },
    { value: 'Website/Form (servefar.com)', label: 'Website/Form (servefar.com)' },
    { value: 'Website/Form (propertyhandling.com)', label: 'Website/Form (propertyhandling.com)' },
    { value: 'Internal Referral', label: 'Internal Referral' },
    { value: 'Direct Call/Message', label: 'Direct Call/Message' },
    { value: 'Whatsapp', label: 'Whatsapp' },
    { value: 'Email', label: 'Email' },
    { value: 'Epoque(Lead Generator)', label: 'Epoque(Lead Generator)' },
    { value: 'Others', label: 'Others' }
  ];

  // Download state
  isDownloading: boolean = false;
  downloadProgress: string = '';
  activeDownloadType: 'pdf' | 'csv' | 'email' | 'whatsapp' | null = null;

  // Share modal state
  showShareModal: boolean = false;
  shareType: 'email' | 'whatsapp' = 'email';
  shareForm!: FormGroup;

  reportTypeOptions = [
    { value: 'pdf', label: 'PDF' },
    { value: 'csv', label: 'CSV' }
  ];

  // Services
  private httpService = inject(HttpServiceService);
  private authService = inject(LocalStorageService);
  loadingService = inject(LoadingService);
  private destroyRef = inject(DestroyRef);

  // Subscriptions
  private allReportSubscription?: Subscription;
  private byStatusSubscription?: Subscription;
  private bySourceSubscription?: Subscription;
  private managerSearchSubscription?: Subscription;

  constructor(
    private fb: FormBuilder,
    private alertService: NgxToastrService
  ) {
    this.initFilterForm();
    this.initShareForm();
  }

  ngOnInit(): void {
    this.setupManagerSearch();
    this.setupDateValidation();
    this.loadAllReport();
    this.loadByStatusReport();
    this.loadBySourceReport();
  }

  private initFilterForm(): void {
    this.filterForm = this.fb.group({
      start_date: [''],
      end_date: [{ value: '', disabled: true }],
      stage: [null],
      status: [null],
      source: [null],
      assigned_to: [null]
    });
  }

  private initShareForm(): void {
    this.shareForm = this.fb.group({
      selectedUser: [null],
      file_type: ['pdf'],
      first_name: [''],
      last_name: [''],
      email: [''],
      country_code: [''],
      whatsapp: ['']
    });
  }

  private setupDateValidation(): void {
    this.filterForm.get('start_date')!.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        const endDateControl = this.filterForm.get('end_date');
        if (value) {
          endDateControl!.enable();
        } else {
          endDateControl!.disable();
          endDateControl!.reset();
        }
      });
  }

  private setupManagerSearch(): void {
    this.managerSearchSubscription = this.searchManager$
      .pipe(
        debounceTime(300),
        tap(() => {
          this.managerPage = 1;
          this.hasMoreManagers = true;
        }),
        switchMap((term) => {
          const search = term ? term : '';
          return this.httpService.getPropertyManagers(this.managerPage, this.managerPageSize, search);
        })
      )
      .subscribe((response) => {
        this.managersList = response.data?.users ?? [];
        this.hasMoreManagers = this.managersList.length < (response.data?.total ?? 0);
        this.managersItems = this.managersList.map((m: any) => ({
          value: m.uid,
          label: `${m.first_name} ${m.last_name}`,
          data: {
            first_name: m.first_name,
            last_name: m.last_name,
            email: m.email,
            phone: m.phone,
            country_code: m.country_code
          }
        }));
      });
    this.searchManager$.next('');
  }

  onManagerScrollEnd(): void {
    if (!this.hasMoreManagers) return;
    this.managerPage++;
    this.httpService.getPropertyManagers(this.managerPage, this.managerPageSize)
      .subscribe((resp) => {
        const more = resp.data?.users ?? [];
        this.managersList = [...this.managersList, ...more];
        this.hasMoreManagers = this.managersList.length < (resp.data?.total ?? 0);
        this.managersItems = this.managersList.map((m: any) => ({
          value: m.uid,
          label: `${m.first_name} ${m.last_name}`,
          data: {
            first_name: m.first_name,
            last_name: m.last_name,
            email: m.email,
            phone: m.phone,
            country_code: m.country_code
          }
        }));
      });
  }

  onShareUserChange(selectedItem: any): void {
    if (selectedItem && selectedItem.data) {
      this.shareForm.patchValue({
        first_name: selectedItem.data.first_name || '',
        last_name: selectedItem.data.last_name || ''
      });
      if (this.shareType === 'email') {
        this.shareForm.patchValue({ email: selectedItem.data.email || '' });
      } else {
        this.shareForm.patchValue({
          country_code: selectedItem.data.country_code || '',
          whatsapp: selectedItem.data.phone || ''
        });
      }
    }
  }

  switchTab(tab: 'all' | 'by-status' | 'by-source' | 'graphs'): void {
    this.activeTab = tab;
  }

  private buildFilterQuery(): string {
    let query = '';
    const formValue = this.filterForm.getRawValue();

    if (formValue.start_date) query += `&start_date=${formValue.start_date}`;
    if (formValue.end_date) query += `&end_date=${formValue.end_date}`;
    if (formValue.stage) query += `&stage=${encodeURIComponent(formValue.stage)}`;
    if (formValue.status) query += `&status=${formValue.status}`;
    if (formValue.source) query += `&source=${encodeURIComponent(formValue.source)}`;
    if (formValue.assigned_to) {
      const uid = typeof formValue.assigned_to === 'object' ? formValue.assigned_to.value : formValue.assigned_to;
      if (uid) query += `&assigned_to=${uid}`;
    }

    if (this.selectedColumnKeys.size && this.availableColumns.length) {
      const cols = this.availableColumns
        .map((c) => c.key)
        .filter((k) => this.selectedColumnKeys.has(k));
      if (cols.length) query += `&columns=${encodeURIComponent(cols.join(','))}`;
    }

    return query;
  }

  get visibleColumns(): { key: string; label: string; wrap?: boolean }[] {
    return this.availableColumns.filter((c) => this.selectedColumnKeys.has(c.key));
  }

  toggleColumn(key: string, checked: boolean): void {
    if (checked) this.selectedColumnKeys.add(key);
    else this.selectedColumnKeys.delete(key);
    this.selectedColumnKeys = new Set(this.selectedColumnKeys);
  }

  toggleColumnPicker(): void {
    this.showColumnPicker = !this.showColumnPicker;
  }

  // The dropdown wrapper calls $event.stopPropagation on its own clicks, so
  // anything that bubbles to the document came from outside it.
  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.showColumnPicker) this.showColumnPicker = false;
  }

  private hydrateColumns(available: { key: string; label: string; default?: boolean; wrap?: boolean }[] | undefined): void {
    if (!available || this.availableColumns.length) return;
    this.availableColumns = available;
    this.selectedColumnKeys = new Set(available.filter((c) => c.default).map((c) => c.key));
  }

  loadAllReport(): void {
    const query = this.buildFilterQuery();
    this.allReportSubscription = this.httpService
      .getLeadsReportAll(this.pageAll, this.totalRowsAll, query)
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.allLeadsData = response.data.rows ?? [];
            this.summaryData = response.data.summary ?? null;
            this.totalPageAll = response.data.total ?? 0;
            this.pageCountAll = response.data.pageCount ?? 1;
            this.hydrateColumns(response.data.available_columns);
          }
        },
        error: () => {
          this.alertService.error('Failed to load leads report', 'toast-top-right');
        }
      });
  }

  loadByStatusReport(): void {
    const query = this.buildFilterQuery();
    this.byStatusSubscription = this.httpService
      .getLeadsReportByStatus(this.pageByStatus, this.totalRowsByStatus, query)
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.byStatusData = response.data.rows ?? [];
            if (!this.summaryData) this.summaryData = response.data.summary ?? null;
            this.totalPageByStatus = response.data.total ?? 0;
            this.pageCountByStatus = response.data.pageCount ?? 1;
          }
        },
        error: () => {
          this.alertService.error('Failed to load by-status report', 'toast-top-right');
        }
      });
  }

  loadBySourceReport(): void {
    const query = this.buildFilterQuery();
    this.bySourceSubscription = this.httpService
      .getLeadsReportBySource(this.pageBySource, this.totalRowsBySource, query)
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.bySourceData = response.data.rows ?? [];
            if (!this.summaryData) this.summaryData = response.data.summary ?? null;
            this.totalPageBySource = response.data.total ?? 0;
            this.pageCountBySource = response.data.pageCount ?? 1;
          }
        },
        error: () => {
          this.alertService.error('Failed to load by-source report', 'toast-top-right');
        }
      });
  }

  onFilterChange(): void {
    this.disableClear = true;
    this.pageAll = 1;
    this.pageByStatus = 1;
    this.pageBySource = 1;
    this.loadAllReport();
    this.loadByStatusReport();
    this.loadBySourceReport();
  }

  resetFilters(): void {
    this.filterForm.reset({
      start_date: '',
      end_date: '',
      stage: null,
      status: null,
      source: null,
      assigned_to: null
    });
    this.disableClear = false;
    this.pageAll = 1;
    this.pageByStatus = 1;
    this.pageBySource = 1;
    this.loadAllReport();
    this.loadByStatusReport();
    this.loadBySourceReport();
  }

  pageChangeAll(page: number): void {
    if (this.pageAll === page) return;
    this.pageAll = page;
    this.loadAllReport();
  }

  pageChangeByStatus(page: number): void {
    if (this.pageByStatus === page) return;
    this.pageByStatus = page;
    this.loadByStatusReport();
  }

  pageChangeBySource(page: number): void {
    if (this.pageBySource === page) return;
    this.pageBySource = page;
    this.loadBySourceReport();
  }

  // Download functionality
  downloadReport(fileType: 'pdf' | 'csv'): void {
    if (this.isDownloading) return;

    this.isDownloading = true;
    this.activeDownloadType = fileType;
    this.downloadProgress = 'Initiating...';

    const query = this.buildFilterQuery();
    let downloadFn;

    if (this.activeTab === 'by-status') {
      downloadFn = this.httpService.downloadLeadsReportByStatus(fileType, query);
    } else if (this.activeTab === 'by-source') {
      downloadFn = this.httpService.downloadLeadsReportBySource(fileType, query);
    } else {
      downloadFn = this.httpService.downloadLeadsReportAll(fileType, query);
    }

    downloadFn.subscribe({
      next: (response) => {
        if (response.success && response.data?.job_id) {
          this.pollJobStatus(response.data.job_id);
        } else {
          this.resetDownloadState();
          this.alertService.error('Failed to initiate download', 'toast-top-right');
        }
      },
      error: () => {
        this.resetDownloadState();
        this.alertService.error('Failed to initiate download', 'toast-top-right');
      }
    });
  }

  private resetDownloadState(): void {
    this.isDownloading = false;
    this.downloadProgress = '';
    this.activeDownloadType = null;
  }

  private pollJobStatus(jobId: string): void {
    const maxPollingTime = 30000;
    const pollingInterval = 1000;
    const startTime = Date.now();
    let pollCount = 0;

    this.checkJobStatus(jobId);

    const poll$ = interval(pollingInterval).pipe(
      takeWhile(() => {
        const elapsed = Date.now() - startTime;
        return elapsed < maxPollingTime && this.isDownloading;
      })
    );

    poll$.subscribe({
      next: () => {
        pollCount++;
        this.downloadProgress = `Checking... (${pollCount})`;
        this.checkJobStatus(jobId);
      },
      complete: () => {
        if (this.isDownloading) {
          this.resetDownloadState();
          this.alertService.error('Report generation timed out. Please try again.', 'toast-top-right');
        }
      }
    });
  }

  private checkJobStatus(jobId: string): void {
    this.httpService.getReportJobStatus(jobId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const jobData: ReportJobStatusResponse = response.data;
          if (jobData.status === 'completed' && jobData.file_url) {
            this.resetDownloadState();
            this.alertService.success('Report generated successfully!', 'toast-top-right');
            window.open(jobData.file_url, '_blank');
          } else if (jobData.status === 'failed') {
            this.resetDownloadState();
            this.alertService.error(jobData.error_message || 'Report generation failed', 'toast-top-right');
          }
        }
      },
      error: () => {}
    });
  }

  openShareModal(type: 'email' | 'whatsapp'): void {
    this.shareType = type;
    this.shareForm.reset({ file_type: 'pdf' });
    this.showShareModal = true;
  }

  closeShareModal(): void {
    this.showShareModal = false;
    this.shareForm.reset({ file_type: 'pdf' });
  }

  submitShare(): void {
    const formValue = this.shareForm.getRawValue();
    const shareValue = this.shareType === 'email' ? formValue.email : formValue.whatsapp;

    if (!shareValue || shareValue.trim() === '') {
      this.alertService.error(
        `Please enter ${this.shareType === 'email' ? 'an email address' : 'a WhatsApp number'}`,
        'toast-top-right'
      );
      return;
    }

    this.closeShareModal();
    this.isDownloading = true;
    this.activeDownloadType = this.shareType;
    this.downloadProgress = 'Sending...';

    const query = this.buildFilterQuery();
    const shareData: any = {
      share_type: this.shareType,
      first_name: formValue.first_name?.trim() || '',
      last_name: formValue.last_name?.trim() || ''
    };

    if (this.shareType === 'email') {
      shareData.email = shareValue.trim();
    } else {
      shareData.country_code = formValue.country_code?.trim() || '';
      shareData.whatsapp = shareValue.trim();
    }

    const fileType = formValue.file_type || 'pdf';
    let downloadFn;

    if (this.activeTab === 'by-status') {
      downloadFn = this.httpService.downloadLeadsReportByStatus(fileType, query, shareData);
    } else if (this.activeTab === 'by-source') {
      downloadFn = this.httpService.downloadLeadsReportBySource(fileType, query, shareData);
    } else {
      downloadFn = this.httpService.downloadLeadsReportAll(fileType, query, shareData);
    }

    downloadFn.subscribe({
      next: (response) => {
        if (response.success && response.data?.job_id) {
          this.pollJobStatusForShare(response.data.job_id);
        } else {
          this.resetDownloadState();
          this.alertService.error('Failed to initiate share', 'toast-top-right');
        }
      },
      error: () => {
        this.resetDownloadState();
        this.alertService.error('Failed to initiate share', 'toast-top-right');
      }
    });
  }

  private pollJobStatusForShare(jobId: string): void {
    const maxPollingTime = 30000;
    const pollingInterval = 1000;
    const startTime = Date.now();
    let pollCount = 0;

    this.checkJobStatusForShare(jobId);

    const poll$ = interval(pollingInterval).pipe(
      takeWhile(() => {
        const elapsed = Date.now() - startTime;
        return elapsed < maxPollingTime && this.isDownloading;
      })
    );

    poll$.subscribe({
      next: () => {
        pollCount++;
        this.downloadProgress = `Processing... (${pollCount})`;
        this.checkJobStatusForShare(jobId);
      },
      complete: () => {
        if (this.isDownloading) {
          this.resetDownloadState();
          this.alertService.error('Share request timed out. Please try again.', 'toast-top-right');
        }
      }
    });
  }

  private checkJobStatusForShare(jobId: string): void {
    this.httpService.getReportJobStatus(jobId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const jobData: ReportJobStatusResponse = response.data;
          if (jobData.status === 'completed') {
            this.resetDownloadState();
            this.alertService.success(`Report shared via ${this.shareType} successfully!`, 'toast-top-right');
          } else if (jobData.status === 'failed') {
            this.resetDownloadState();
            this.alertService.error(jobData.error_message || 'Failed to share report', 'toast-top-right');
          }
        }
      },
      error: () => {}
    });
  }

  get checkDownloadPermission(): boolean {
    return this.authService.checkPermission(USER_PERMISSIONS.REPORTS.REPORTS_DOWNLOAD);
  }

  getStatusClass(status: string): string {
    const statusClasses: { [key: string]: string } = {
      'New': 'badge-primary',
      'Reached Out': 'badge-info',
      'Introduction Done': 'badge-warning',
      'Draft Agreement Sent': 'badge-warning',
      'Final Agreement Sent': 'badge-secondary',
      'Final Agreement Signed': 'badge-success',
      'Token Amount Paid': 'badge-success'
    };
    return statusClasses[status] || 'badge-secondary';
  }

  getSourceClass(source: string): string {
    // Website/Form leads come back split by website, e.g.
    // "Website/Form (servefar.com)" — colour them like the leads page.
    const website = this.sourceWebsite(source);
    if (website.includes('servefar')) return 'lead-source-servefar';
    if (website.includes('propertyhandling')) return 'lead-source-propertyhandling';
    if (website) return 'badge-info';
    const sourceClasses: { [key: string]: string } = {
      'Website/Form': 'badge-primary',
      'Internal Referral': 'badge-warning',
      'Direct Call/Message': 'badge-success',
      'Whatsapp': 'badge-success',
      'Email': 'badge-info',
      'Epoque(Lead Generator)': 'badge-info',
      'Others': 'badge-secondary'
    };
    return sourceClasses[source] || 'badge-secondary';
  }

  /** Bar colour for the "Leads by Source" chart. */
  getSourceBarClass(source: string): string {
    const website = this.sourceWebsite(source);
    if (website.includes('servefar')) return 'lead-bar-servefar';
    if (website.includes('propertyhandling')) return 'lead-bar-propertyhandling';
    if (website) return 'bg-info';
    const barClasses: { [key: string]: string } = {
      'Website/Form': 'bg-primary',
      'Internal Referral': 'bg-warning',
      'Direct Call/Message': 'bg-success',
      'Whatsapp': 'bg-success',
      'Email': 'bg-info',
      'Epoque(Lead Generator)': 'bg-info',
      'Others': 'bg-secondary'
    };
    return barClasses[source] || 'bg-secondary';
  }

  /** "Website/Form (servefar.com)" → "servefar.com"; '' for other sources. */
  private sourceWebsite(source: string): string {
    const match = /^Website\/Form \(([^()]+)\)$/.exec(source || '');
    return match ? match[1].toLowerCase() : '';
  }

  getSummaryKeys(obj: { [key: string]: number } | undefined): string[] {
    return obj ? Object.keys(obj) : [];
  }

  Math = Math;

  ngOnDestroy(): void {
    this.allReportSubscription?.unsubscribe();
    this.byStatusSubscription?.unsubscribe();
    this.bySourceSubscription?.unsubscribe();
    this.managerSearchSubscription?.unsubscribe();
  }
}
